import { Check, Loader2, QrCode } from 'lucide-react';

import { PreviewHeader } from '../PreviewHeader';
import { AadhaarCardMock } from './AadhaarCardMock';

const STEPS = [
  'Open the mAadhaar / Aadhaar app on your phone.',
  'Scan the QR code shown here.',
  'Approve the verification request in the app.',
  'No Aadhaar number is shared — only the requested details.',
];

// Aadhaar OVSE (Offline Verification via Aadhaar App). Mirrors the PAN screen
// layout; card + content changed. Privacy-first: QR / app approval, no OTP.
//   `large`   — session-sized card (builder phone preview uses the small default).
//   `qrSrc`   — the REAL scannable QR (data URI) from the provider, shown after
//               the applicant starts. When set, the box shows a live QR + a
//               "waiting for approval" hint and the steps become active.
//   default   — decorative placeholder (builder preview) / "starts after tap"
//               hint (session, before start) — never a scannable-looking fake.
export function AadhaarOvseScreen({
  large = false,
  qrSrc,
}: {
  large?: boolean;
  qrSrc?: string | null;
}) {
  return (
    <>
      <PreviewHeader title="Aadhaar Verification" subtitle="Verify using your Aadhaar App" />

      <div className="mb-2 w-full">
        <AadhaarCardMock large={large} />
      </div>

      {/* QR block */}
      <div className="mb-3 flex flex-col items-center gap-2 rounded-2xl border border-[#99B6FF] bg-[#E5EDFF] px-6 py-4">
        <div className="flex h-[140px] w-[140px] items-center justify-center overflow-hidden rounded-lg bg-white">
          {qrSrc ? (
            <img
              src={qrSrc}
              alt="Aadhaar verification QR"
              className="h-full w-full object-contain p-1"
            />
          ) : (
            <QrCode size={72} strokeWidth={1.5} className="text-[#C7D2FE]" />
          )}
        </div>
        {qrSrc ? (
          <span className="flex items-center gap-1.5 text-[length:var(--text-12)] font-medium text-primary">
            <Loader2 size={12} className="animate-spin" />
            Scan with the Aadhaar App — waiting for approval…
          </span>
        ) : (
          <span className="text-[length:var(--text-12)] text-gray30">
            QR appears after you start verification
          </span>
        )}
      </div>

      <ul className="mb-2 flex flex-col gap-2.5">
        {STEPS.map((text) => (
          <li
            key={text}
            className="flex items-start gap-2 text-[length:var(--text-12)] leading-tight text-gray30"
          >
            <div className="flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-full bg-primary text-white">
              <Check size={10} strokeWidth={4} />
            </div>
            <span>{text}</span>
          </li>
        ))}
      </ul>

      <p className="mb-3 mt-2 text-[11px] leading-snug text-[#6B7280]">
        Offline verification via UIDAI — your Aadhaar number is never shared. We only receive the
        details you consent to.
      </p>
    </>
  );
}
